import { useEffect, useLayoutEffect, useRef, useState } from "react";

// A press that takes focus from the field blurs it between its mousedown and
// its mouseup. Folding the field back into a line of text right then moves
// whatever is under the finger, and the browser delivers the click to nothing.
// So the commit happens on blur, but the fold waits until the press that
// caused it has been released and its click delivered (the click is
// dispatched in the same task as the mouseup).
let pressInFlight = false;
const afterPress = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("mousedown", () => { pressInFlight = true; }, true);
  window.addEventListener("mouseup", () => {
    pressInFlight = false;
    const waiting = [...afterPress];
    afterPress.clear();
    if (waiting.length) setTimeout(() => waiting.forEach((run) => run()), 0);
  }, true);
}
function whenPressSettles(run) {
  if (pressInFlight) afterPress.add(run);
  else run();
}

function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 5.5h10M5 9.5h7M5 13.5h5M16.5 11.5l3 3-5.5 5.5-3.5.5.5-3.5 5.5-5.5Z" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 20h4L19 9l-4-4L4 16v4ZM13.5 6.5l4 4" />
    </svg>
  );
}

// The only editable content of the FINISHED Review: one note (the workout's or
// an exercise's). Resting and empty, it is a quiet "Add note" action. Resting
// with a note, it is an annotation set off by a hairline, in two levels: a
// header (the NOTE micro-label and the one icon, the edit pencil) and the text
// below it. A long note shows three lines and a quiet "Show more"; that is
// presentation only, the stored note is never shortened.
// It becomes a field only when the athlete asks for it.
// `onCommit` receives the new note, or null when it was cleared; it is called
// only when the text differs from the committed value.
export default function ReviewNote({
  id,
  label,
  addLabel,
  // Accessible name of the add action where several share one screen, e.g.
  // "Add note for Incline Dumbbell Press". The visible text stays `addLabel`.
  addAriaLabel,
  // Accessible name of the icon-only edit action, e.g. "Edit workout note" or
  // "Edit note for Incline Dumbbell Press".
  editLabel,
  value,
  maxLength,
  disabled = false,
  onCommit,
}) {
  const committed = value || "";
  const [draft, setDraft] = useState(committed);
  const [editing, setEditing] = useState(false);
  const fieldRef = useRef(null);
  const openerRef = useRef(null);
  const returnFocusRef = useRef(false);
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // A changed note starts collapsed again.
  useEffect(() => {
    setExpanded(false);
  }, [committed]);

  // Whether the collapsed text is cut is read from the layout, so it follows
  // the real line count at any width. While expanded nothing is cut and the
  // last answer stands (the toggle must stay to collapse again).
  useLayoutEffect(() => {
    const text = textRef.current;
    if (!text || expanded || editing) return undefined;
    const measure = () => setOverflows(text.scrollHeight > text.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(text);
    return () => observer.disconnect();
  }, [committed, expanded, editing]);

  // Resync the draft only when the committed value changes, so a background
  // sync reload never clobbers text being typed.
  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  // Focus moves in the same task as the tap that opened the field (iOS only
  // raises the keyboard then).
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!editing || !field) return;
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }, [editing]);

  // Closing hands focus back to the action that opened the field. That action
  // can be replaced once the commit lands ("Add note" becomes "Edit"), so
  // focus is handed back until it rests on the action or the athlete has
  // moved it elsewhere.
  useLayoutEffect(() => {
    if (!returnFocusRef.current || editing) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== openerRef.current) {
      returnFocusRef.current = false;
      return;
    }
    openerRef.current?.focus?.();
  });

  // The field is as tall as its text.
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!editing || !field) return;
    field.style.height = "auto";
    // scrollHeight leaves the borders out; without them the field is a hair
    // short and shows its own scrollbar.
    field.style.height = `${field.scrollHeight + field.offsetHeight - field.clientHeight}px`;
  }, [draft, editing]);

  function commit() {
    if (draft !== committed) onCommit?.(draft || null);
  }

  // Done: commit and fold. Activated from the keyboard, focus goes back to
  // the action that opened the field; a tap needs no focus to return to (and
  // would only draw a focus ring on it).
  function done(event) {
    commit();
    returnFocusRef.current = event?.detail === 0;
    setEditing(false);
  }

  // Focus left the field (another control, or the keyboard's own dismiss).
  function leave() {
    commit();
    whenPressSettles(() => setEditing(false));
  }

  if (editing && !disabled) {
    return (
      <div
        className={`lz-workout-review-note lz-workout-review-note--editing${
          committed ? " lz-workout-review-note--annotation" : ""
        }`}
      >
        <label className="lz-v2-visually-hidden" htmlFor={id}>{label}</label>
        <textarea
          ref={fieldRef}
          id={id}
          rows={1}
          maxLength={maxLength}
          value={draft}
          placeholder={addLabel}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={leave}
        />
        <button
          type="button"
          className="lz-workout-review-note__done"
          // Keeps focus in the field for the length of the press, so this
          // click is the one event that closes the editor.
          onMouseDown={(event) => event.preventDefault()}
          onClick={done}
        >
          Done
        </button>
      </div>
    );
  }

  if (!committed) {
    return (
      <div className="lz-workout-review-note">
        <button
          ref={openerRef}
          type="button"
          className="lz-workout-review-note__add"
          disabled={disabled}
          aria-label={addAriaLabel || undefined}
          onBlur={() => { returnFocusRef.current = false; }}
          onClick={() => setEditing(true)}
        >
          <NoteIcon /> {addLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="lz-workout-review-note lz-workout-review-note--filled lz-workout-review-note--annotation">
      <div className="lz-workout-review-note__head">
        <span className="lz-workout-review-note__label">Note</span>
        <button
          ref={openerRef}
          type="button"
          className="lz-workout-review-note__edit"
          disabled={disabled}
          aria-label={editLabel || `Edit ${label.toLowerCase()}`}
          onBlur={() => { returnFocusRef.current = false; }}
          onClick={() => setEditing(true)}
        >
          <EditIcon />
        </button>
      </div>
      <p
        ref={textRef}
        id={`${id}-text`}
        className="lz-workout-review-note__text"
        data-clamped={expanded ? undefined : "true"}
      >
        {committed}
      </p>
      {overflows || expanded ? (
        <button
          type="button"
          className="lz-workout-review-note__more"
          aria-expanded={expanded}
          aria-controls={`${id}-text`}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </div>
  );
}
